import { useEffect } from 'react';
import { BUTTON_THEMES, Form, InputField, LoaderComponent } from 'ht-styleguide';
import { useForm, SubmitHandler } from 'react-hook-form';
import has from 'lodash/has';

/* Store */
import { useHomeHubStoreActions, useHomeHubStoreUserFlowAttributes } from '@scenes/HomeHub/store/client/homehub.store';

/* Queries/Mutations */
import useUserMutationCheckUserEmail from '@store/queries/user/useUserMutation.checkUserEmail';
import useUserMutationRegister, { TRegisterShortProps as YupFormProps } from '@store/queries/user/useUserMutation.register';

/* Hooks */
import useRouteController from '@scenes/HomeHub/hooks/hook.routeController';

/* Components */
import UserFlowHeader from '@scenes/HomeHub/pages/User/components/Header';
import { GridLayoutForm } from '@scenes/HomeHub/pages/User/components/GridLayouts';
import ButtonWrapper from '@scenes/HomeHub/pages/User/components/ButtonWrapper';
import SubduedText from '@scenes/HomeHub/pages/User/components/SubduedText';
import Typography from '@scenes/HomeHub/pages/User/components/Typography';
import Either from '@components/Either';

/* Constants / Utils */
import { YupSchemaResolver } from '@constants/validations';
import { setErrorsHookFormErrors } from '@utils/errors';
import { UserScreenTypes } from '@scenes/HomeHub/pages/User/index';

import styles from '@scenes/HomeHub/pages/User/user.module.scss';
import { LEAD_GEN_SOURCE_TYPES, LeadGeneratedTrackingEvent } from '@integrations/segment/leadgen';
import { useRouter } from 'next/router';

const UserEmailStep = () => {
  /* Store */
  const { userEmail, firstName } = useHomeHubStoreUserFlowAttributes();
  /* Store Actions */
  const { setAttributeInUserFlow } = useHomeHubStoreActions();

  /* Queries/mutations */
  /* when email exists the query will run */
  const { data, mutate, isPending, isSuccess } = useUserMutationCheckUserEmail();
  const { mutate: mutateReg, isPending: isLoadingReg } = useUserMutationRegister<YupFormProps>('survey');

  /* Hooks / Store */
  const { nextUserRoute } = useRouteController();
  const {
    register,
    handleSubmit,
    setError,
    getValues,
    formState: { errors, isDirty },
  } = useForm<YupFormProps>({ ...YupSchemaResolver(['email'], undefined, { email: userEmail }) });
  const router = useRouter();

  /* Constants */
  const deriveCategory = router.asPath.split('/').filter(Boolean)?.[1] ?? '';

  /* Methods */
  const registerFormAttributes: SubmitHandler<YupFormProps> = attributes => {
    mutate(attributes);
  };

  /* Resaturate Errors */
  useEffect(() => {
    if (data?.formErrors) {
      setErrorsHookFormErrors<YupFormProps>({ setError, mutationData: data });
    }
  }, [data?.formErrors, data, setError]);

  /* What is the status of the email */
  useEffect(() => {
    if (!isSuccess) return;
    /*
      Two steps
        1. have user enter password page
        2. have user & needs pw, enter new password page
    */
    if (data?.user) {
      setAttributeInUserFlow({ email: getValues('email'), data });

      if (data.rpt) {
        /* User but no PW on account, set password */
        nextUserRoute(UserScreenTypes.setPassword);
      } else {
        /* existing user, enter password */
        nextUserRoute(UserScreenTypes.password);
      }
    } else {
      /*
        We want to capture the user into our system. Register them.
        Note: Seed phone number with 1112223333, initial step. Then override next screen. Marketing wants email capture asap.
      */
      LeadGeneratedTrackingEvent({
        email: getValues('email'),
        sourceGroup: {
          name: deriveCategory,
          type: LEAD_GEN_SOURCE_TYPES.homehub.type,
        },
      });

      mutateReg(
        { first_name: firstName, email: data.email },
        {
          onSuccess: () => {
            setAttributeInUserFlow({ email: getValues('email') });
            nextUserRoute(UserScreenTypes.setPasswordRegister);
          },
        },
      );
    }
  }, [isSuccess, data, setError, deriveCategory, firstName, getValues, mutateReg, nextUserRoute, setAttributeInUserFlow]);

  return (
    <GridLayoutForm>
      <Form.Row classes="marginAuto width100">
        <Form.Column sm={4} md={6} mdOffset={1} lg={6} lgOffset={3}>
          <UserFlowHeader headerText="What is your email address?" />
          <Typography className="p1 n700 paddingTop-tiny1">
            We'll use this to save your recommendations so you can securely access them later.
          </Typography>
        </Form.Column>
      </Form.Row>
      <Form.Row classes="marginAuto width100">
        <Form.Column sm={4} md={6} mdOffset={1} lg={4} lgOffset={4}>
          <Either condition={isPending || isLoadingReg || has(data, 'user')}>
            <div className="marginTop-small">
              <LoaderComponent />
            </div>

            <div className={styles.actionContainer}>
              <InputField type="text" label="" error={errors.email?.message} {...register('email')} />
              <ButtonWrapper
                className="paddingTop-small1"
                disabled={!isDirty}
                onClick={handleSubmit(registerFormAttributes)}
                theme={BUTTON_THEMES.PRIMARY}
              >
                Continue
              </ButtonWrapper>
              <SubduedText classes="caption paddingTop-small">
                By clicking "Continue" you agree to receive automated marketing messages from HelloTech about our recommendations and other offers.
              </SubduedText>
            </div>
          </Either>
        </Form.Column>
      </Form.Row>
    </GridLayoutForm>
  );
};

export default UserEmailStep;
